import { useState, useEffect } from 'react';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { useApiRequest } from '../../hooks/useApiRequest';

interface EmailNotificationSettings {
  enableInterestEmails: boolean;
  enableMessageEmails: boolean;
  enableSuperInterestEmails: boolean;
}

export default function AdminEmailNotifications() {
  const [settings, setSettings] = useState<EmailNotificationSettings>({
    enableInterestEmails: true,
    enableMessageEmails: true,
    enableSuperInterestEmails: true,
  });
  const { loading, execute } = useApiRequest();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const response = await execute('/settings');
    if (response?.success) {
      const emailNotifications = response.data.settings?.emailNotifications || {};
      setSettings({
        enableInterestEmails: emailNotifications.enableInterestEmails ?? true,
        enableMessageEmails: emailNotifications.enableMessageEmails ?? true,
        enableSuperInterestEmails: emailNotifications.enableSuperInterestEmails ?? true,
      });
    }
  };

  const handleToggle = (key: keyof EmailNotificationSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    const response = await execute('/admin/settings/email-notifications', {
      method: 'PUT',
      data: settings
    });

    if (response?.success) {
      toast.success('Email notification settings updated successfully');
      await fetchSettings();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl">Email Notifications</h1>
        <p className="text-muted-foreground mt-1">
          Control which email notifications are sent to users
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            <CardTitle>Email Notification Settings</CardTitle>
          </div>
          <CardDescription>
            Enable or disable email notifications for different user interactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Interest Emails */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label className="text-base">Interest Emails</Label>
              <p className="text-sm text-muted-foreground">
                Send email when a user receives an interest from another user
              </p>
            </div>
            <Switch
              checked={settings.enableInterestEmails}
              onCheckedChange={() => handleToggle('enableInterestEmails')}
              disabled={loading}
            />
          </div>

          {/* Super Interest Emails */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label className="text-base">Super Interest Emails</Label>
              <p className="text-sm text-muted-foreground">
                Send email when a user receives a super interest (super like)
              </p>
            </div>
            <Switch
              checked={settings.enableSuperInterestEmails}
              onCheckedChange={() => handleToggle('enableSuperInterestEmails')}
              disabled={loading}
            />
          </div>

          {/* Message Emails */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <Label className="text-base">Message Emails</Label>
              <p className="text-sm text-muted-foreground">
                Send email when a user receives a new message
              </p>
            </div>
            <Switch
              checked={settings.enableMessageEmails}
              onCheckedChange={() => handleToggle('enableMessageEmails')}
              disabled={loading}
            />
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Email Configuration</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Emails are sent using SMTP configured in backend .env file</li>
                  <li>Users will receive emails at their registered email address</li>
                  <li>Email templates include sender name and relevant information</li>
                  <li>Emails are sent asynchronously to avoid blocking requests</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Status Indicator */}
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-900">
                <p className="font-semibold mb-1">SMTP Service Status</p>
                <p>Email service is configured and ready. Check backend logs for SMTP initialization status.</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
